import { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useCases } from "@/hooks/useCases";
import { useT } from "@/lib/i18n";
import { useTranslatedTexts } from "@/hooks/useTranslatedTexts";

/**
 * Historical hantavirus outbreak comparison.
 * Sources: WHO DON, CDC MMWR, PAHO, NEJM, ANLIS Malbrán.
 *
 * UI source language is English. Strings are auto-translated to the active
 * language via useTranslatedTexts (Supabase translate-batch edge function).
 */

type Outbreak = {
  key: string;
  name: string;
  yearLabel: string;
  variant: string;
  location: string;
  cases: number;
  deaths: number;
  cfr: number;
  countries: number;
  status: "active" | "endemic" | "historical";
  source: string;
  sourceUrl: string;
  note: string;
  trajectory?: { day: number; cases: number }[];
};

// NOTE: `cases` here means non-fatal cases only. Total cases = cases + deaths.
// CFR = deaths / (cases + deaths). Hard-coded values reconciled so total + cfr
// match published sources (NEJM, CDC MMWR, PAHO).
const HISTORICAL: Outbreak[] = [
  {
    key: "epuyen-2018",
    name: "Epuyén cluster",
    yearLabel: "2018–19",
    variant: "ANDV",
    location: "Chubut, Argentina",
    cases: 23, // 34 total − 11 deaths
    deaths: 11,
    cfr: 32.4,
    countries: 1,
    status: "historical",
    source: "NEJM 2020 (Martínez et al.)",
    sourceUrl: "https://www.nejm.org/doi/10.1056/NEJMoa2009040",
    note: "First major documented human-to-human ANDV transmission.",
    trajectory: [
      { day: 0, cases: 1 }, { day: 7, cases: 2 }, { day: 14, cases: 5 },
      { day: 21, cases: 12 }, { day: 28, cases: 20 }, { day: 35, cases: 26 },
      { day: 49, cases: 31 }, { day: 70, cases: 34 },
    ],
  },
  {
    key: "yosemite-2012",
    name: "Yosemite",
    yearLabel: "2012",
    variant: "SNV",
    location: "Curry Village, USA",
    cases: 7, // 10 total − 3 deaths
    deaths: 3,
    cfr: 30,
    countries: 1,
    status: "historical",
    source: "CDC MMWR 61(46)",
    sourceUrl: "https://www.cdc.gov/mmwr/preview/mmwrhtml/mm6146a5.htm",
    note: "Mouse nesting in double-walled signature tents.",
    trajectory: [
      { day: 0, cases: 1 }, { day: 7, cases: 2 }, { day: 14, cases: 4 },
      { day: 21, cases: 7 }, { day: 28, cases: 10 },
    ],
  },
  {
    key: "chile-2011",
    name: "Chile ANDV",
    yearLabel: "2011–12",
    variant: "ANDV",
    location: "Aysén, Chile",
    cases: 14, // 22 total − 8 deaths
    deaths: 8,
    cfr: 36.4,
    countries: 1,
    status: "endemic",
    source: "PAHO / Chile Min. Salud",
    sourceUrl: "https://www.paho.org/en/topics/hantavirus",
    note: "ANDV is endemic in Chile, ~50–80 cases/yr, CFR ~30%.",
  },
  {
    key: "argentina-1996",
    name: "First ANDV human-to-human",
    yearLabel: "1996",
    variant: "ANDV",
    location: "El Bolsón, Argentina",
    cases: 10, // 20 total − 10 deaths
    deaths: 10,
    cfr: 50,
    countries: 2,
    status: "historical",
    source: "Wells et al., EID 1997",
    sourceUrl: "https://wwwnc.cdc.gov/eid/article/3/2/97-0210_article",
    note: "First documented evidence of human-to-human spread.",
  },
  {
    key: "fourcorners-1993",
    name: "Four Corners",
    yearLabel: "1993",
    variant: "SNV",
    location: "US Southwest",
    cases: 21, // 48 total − 27 deaths
    deaths: 27,
    cfr: 56.3,
    countries: 1,
    status: "historical",
    source: "Nichol et al., Science 1993",
    sourceUrl: "https://www.cdc.gov/hantavirus/outbreaks/history.html",
    note: "First identification of Sin Nombre virus and HPS.",
    trajectory: [
      { day: 0, cases: 1 }, { day: 7, cases: 8 }, { day: 14, cases: 18 },
      { day: 21, cases: 32 }, { day: 28, cases: 44 }, { day: 35, cases: 48 },
    ],
  },
];

// Distinct color palette — each outbreak clearly distinguishable
const COLOR: Record<string, string> = {
  "hondius-2026": "#ef4444",   // red — current
  "epuyen-2018":  "#06b6d4",   // cyan
  "yosemite-2012":"#eab308",   // amber
  "chile-2011":   "#10b981",   // emerald
  "argentina-1996":"#a855f7",  // purple
  "fourcorners-1993":"#f472b6",// pink
};

// Dash patterns — make every historical line dashed so the solid red current
// outbreak stands out unmistakably.
const DASH: Record<string, string | undefined> = {
  "hondius-2026":  undefined,    // solid
  "epuyen-2018":   "6 4",
  "yosemite-2012": "3 4",
  "fourcorners-1993":"2 6",
  "chile-2011":    "8 4",
  "argentina-1996":"4 2",
};

type Tab = "trajectory" | "cfr" | "table";

// ─────────────────────────────────────────────────────────────────────────────
// UI strings (English source). Index-based so we can pull from the translated
// array safely.
const UI = {
  EYEBROW: "Historical Context",
  INTRO: "Hantavirus has caused deadly outbreaks before — from 1993 Four Corners to recurring Andes virus clusters.",
  CASES_VS: "cases vs. historical outbreaks",
  CFR_NOW: "CFR now",
  COUNTRIES: "countries",
  DAYS: "days",
  TAB_GROWTH: "Growth",
  TAB_CFR: "CFR",
  TAB_TABLE: "Table",
  LIVE_LABEL: "Now · MV Hondius 2026",
  VS_HISTORICAL: "vs. historical outbreaks:",
  DAY: "Day",
  ACTIVE: "Active",
  COL_OUTBREAK: "Outbreak",
  COL_CASES: "Cases",
  COL_DEATHS: "Deaths",
  COL_CFR: "CFR",
  COL_COUNTRIES: "Countries",
  SOURCE: "Source",
  CFR_TITLE: "Case fatality rate (CFR) by outbreak",
  CFR_HINT: "Higher = deadlier. CFR computed on lab-confirmed cases only. Active outbreak shown in red.",
  CONFIRMED: "confirmed",
  SUSPECTED: "suspected",
  SUSP_SHORT: "susp.",
  COMPARE_NOTE: "Comparison uses lab-confirmed + fatal cases only — historical outbreaks are confirmed counts.",
} as const;

type UIKey = keyof typeof UI;
const UI_KEYS = Object.keys(UI) as UIKey[];

// ─────────────────────────────────────────────────────────────────────────────

export default function OutbreakComparison() {
  const [tab, setTab] = useState<Tab>("trajectory");
  const { cases: liveCases } = useCases();
  const { lang } = useT();

  // Translate all UI strings + outbreak names/locations/notes/sources at once.
  const baseTexts = useMemo(() => UI_KEYS.map((k) => UI[k]), []);
  const outbreakTexts = useMemo(() => {
    const arr: string[] = [];
    HISTORICAL.forEach((o) => {
      arr.push(o.name, o.location, o.note, o.source);
    });
    arr.push("MV Hondius", "Cruise ship cluster", "Ongoing outbreak, refreshed every 5 minutes.", "WHO DON599 / RIVM (live)");
    return arr;
  }, []);
  const allTexts = useMemo(() => [...baseTexts, ...outbreakTexts], [baseTexts, outbreakTexts]);
  const translated = useTranslatedTexts(allTexts, lang);
  const tr = (k: UIKey) => translated[UI_KEYS.indexOf(k)] ?? UI[k];
  const trOutbreak = useMemo(() => {
    const offset = baseTexts.length;
    const map = new Map<string, { name: string; location: string; note: string; source: string }>();
    HISTORICAL.forEach((o, i) => {
      const base = offset + i * 4;
      map.set(o.key, {
        name: translated[base] ?? o.name,
        location: translated[base + 1] ?? o.location,
        note: translated[base + 2] ?? o.note,
        source: translated[base + 3] ?? o.source,
      });
    });
    const liveBase = offset + HISTORICAL.length * 4;
    map.set("hondius-2026", {
      name: translated[liveBase] ?? "MV Hondius",
      location: translated[liveBase + 1] ?? "Cruise ship cluster",
      note: translated[liveBase + 2] ?? "Ongoing outbreak, refreshed every 5 minutes.",
      source: translated[liveBase + 3] ?? "WHO DON599 / RIVM (live)",
    });
    return map;
  }, [translated, baseTexts.length]);

  const current = useMemo(() => {
    const cases = liveCases ?? [];
    const total = cases.length;
    // Confirmed-equivalent cases = lab-confirmed + fatal. These are what
    // historical outbreak counts represent, so this is the apples-to-apples
    // number used for trajectory + CFR.
    const confirmedCases = cases.filter((c) => c.status === "confirmed");
    const confirmed = confirmedCases.length;
    const suspected = cases.filter(
      (c) => c.status === "suspected" || c.status === "monitoring"
    ).length;
    const deaths = confirmedCases.reduce((sum, c) => sum + (c.deaths ?? 0), 0);
    const countries = new Set(cases.map((c) => c.country)).size;
    const dates = confirmedCases
      .map((c) => c.onsetDate ?? c.effectiveDate)
      .filter(Boolean) as string[];
    let days = 0;
    if (dates.length) {
      const min = new Date(dates.reduce((a, b) => (a < b ? a : b)));
      days = Math.max(1, Math.round((Date.now() - min.getTime()) / 86400000));
    }
    // Deaths are counted as separate cases. Total = confirmed + deaths,
    // CFR = deaths / (confirmed + deaths).
    const cfr = confirmed + deaths > 0 ? (deaths / (confirmed + deaths)) * 100 : 0;

    const sorted = [...dates].sort();
    const start = sorted[0] ? new Date(sorted[0]) : null;
    const traj: { day: number; cases: number }[] = [];
    if (start) {
      const counts = new Map<number, number>();
      for (const d of sorted) {
        const day = Math.round((new Date(d).getTime() - start.getTime()) / 86400000);
        counts.set(day, (counts.get(day) ?? 0) + 1);
      }
      let cum = 0;
      // Always extend the trajectory to "today" so the line keeps reaching the
      // present day even when no new onsets have been reported recently.
      const todayDay = Math.max(
        0,
        Math.round((Date.now() - start.getTime()) / 86400000)
      );
      const maxDay = Math.max(todayDay, ...counts.keys());
      for (let i = 0; i <= maxDay; i++) {
        cum += counts.get(i) ?? 0;
        traj.push({ day: i, cases: cum });
      }
    }
    return {
      total,
      confirmed,
      suspected,
      deaths,
      countries,
      days,
      cfr,
      traj,
      startDate: start,
    };
  }, [liveCases]);

  const liveOutbreak: Outbreak = useMemo(() => {
    const t = trOutbreak.get("hondius-2026");
    return {
      key: "hondius-2026",
      name: t?.name ?? "MV Hondius",
      yearLabel: "2026",
      variant: "ANDV",
      location: t?.location ?? "Cruise ship cluster",
      cases: current.confirmed,
      deaths: current.deaths,
      cfr: current.cfr,
      countries: current.countries,
      status: "active",
      source: t?.source ?? "WHO DON599 / RIVM (live)",
      sourceUrl: "https://www.who.int/emergencies/disease-outbreak-news",
      note: t?.note ?? "",
      trajectory: current.traj,
    };
  }, [current, trOutbreak]);

  // Apply translations to historical outbreaks too
  const localizedHistorical: Outbreak[] = useMemo(
    () =>
      HISTORICAL.map((o) => {
        const t = trOutbreak.get(o.key);
        return t ? { ...o, name: t.name, location: t.location, note: t.note, source: t.source } : o;
      }),
    [trOutbreak]
  );

  const all = [liveOutbreak, ...localizedHistorical];

  return (
    <section
      aria-label="Historical outbreak comparison"
      className="border-t border-border bg-background text-foreground"
    >
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-data mb-2">
              {tr("EYEBROW")}
            </div>
            <p className="text-sm text-muted-foreground mb-4 max-w-2xl leading-relaxed">
              {tr("INTRO")}
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-[1.1]">
              {current.confirmed + current.deaths}
              <span className="text-muted-foreground text-lg ml-2 font-normal">
                {tr("CONFIRMED")}
              </span>
              {current.deaths > 0 && (
                <span className="text-muted-foreground text-lg ml-2 font-normal">
                  <span className="opacity-50">·</span>{" "}
                  <span className="text-[#ef4444]">{current.deaths} deaths</span>
                </span>
              )}
              {current.suspected > 0 && (
                <span className="text-muted-foreground text-lg ml-2 font-normal">
                  <span className="opacity-50">+</span> {current.suspected}{" "}
                  <span className="text-[#eab308]">{tr("SUSPECTED")}</span>
                </span>
              )}
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              {tr("CFR_NOW")}: <span className="text-foreground font-medium">{current.cfr.toFixed(1)}%</span>
              <span className="mx-2 opacity-30">·</span>
              {current.countries} {tr("COUNTRIES")}
              <span className="mx-2 opacity-30">·</span>
              {current.days} {tr("DAYS")}
            </p>
            <p className="text-[11px] text-muted-foreground/80 mt-2 max-w-md">
              {tr("COMPARE_NOTE")}
            </p>
          </div>

          <Toggle
            options={[
              { v: "trajectory", label: tr("TAB_GROWTH") },
              { v: "cfr", label: tr("TAB_CFR") },
              { v: "table", label: tr("TAB_TABLE") },
            ]}
            value={tab}
            onChange={(v) => setTab(v as Tab)}
          />
        </div>

        {/* Views */}
        {tab === "trajectory" && (
          <TrajectoryView outbreaks={all} liveLabel={tr("LIVE_LABEL")} vsLabel={tr("VS_HISTORICAL")} dayLabel={tr("DAY")} liveStartDate={current.startDate} lang={lang} />
        )}
        {tab === "cfr" && <CfrView outbreaks={all} title={tr("CFR_TITLE")} hint={tr("CFR_HINT")} />}
        {tab === "table" && (
          <TableView
            outbreaks={all}
            cols={{
              outbreak: tr("COL_OUTBREAK"),
              cases: tr("COL_CASES"),
              deaths: tr("COL_DEATHS"),
              cfr: tr("COL_CFR"),
              countries: tr("COL_COUNTRIES"),
            }}
            activeLabel={tr("ACTIVE")}
            suspectedCount={current.suspected}
            suspShortLabel={tr("SUSP_SHORT")}
          />
        )}

        {/* Footer sources */}
        <div className="grid grid-cols-3 gap-3 mt-8 pt-6 border-t border-border">
          <SourceLink label="WHO" sub={tr("SOURCE")} url="https://www.who.int/emergencies/disease-outbreak-news" accent="#ef4444" />
          <SourceLink label="CDC" sub={tr("SOURCE")} url="https://www.cdc.gov/hantavirus/outbreaks/history.html" accent="#f97316" />
          <SourceLink label="PAHO / NEJM" sub={tr("SOURCE")} url="https://www.paho.org/en/topics/hantavirus" accent="var(--muted-foreground)" />
        </div>
      </div>
    </section>
  );
}

function Toggle({
  options,
  value,
  onChange,
}: {
  options: { v: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="inline-flex border border-border rounded-full p-0.5 bg-card/40 self-start">
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className={`px-3 py-1 text-[11px] uppercase tracking-wider font-data rounded-full transition ${
              active
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SourceLink({ label, sub, url, accent }: { label: string; sub: string; url: string; accent: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="border-l-2 pl-3 hover:opacity-80 transition"
      style={{ borderColor: accent }}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{sub}</div>
      <div className="text-sm md:text-base font-semibold font-data text-foreground mt-0.5">
        {label} ↗
      </div>
    </a>
  );
}

function TrajectoryView({
  outbreaks,
  liveLabel,
  vsLabel,
  dayLabel,
  liveStartDate,
  lang,
}: {
  outbreaks: Outbreak[];
  liveLabel: string;
  vsLabel: string;
  dayLabel: string;
  liveStartDate: Date | null;
  lang: string;
}) {
  const dateFmt = (offsetDays: number) => {
    if (!liveStartDate) return null;
    const d = new Date(liveStartDate.getTime() + offsetDays * 86400000);
    try {
      return new Intl.DateTimeFormat(lang || "en", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(d);
    } catch {
      return d.toISOString().slice(0, 10);
    }
  };
  const allSeries = outbreaks.filter((o) => o.trajectory && o.trajectory.length > 0);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const series = allSeries.filter((s) => s.key === "hondius-2026" || !hidden.has(s.key));

  const maxDay = Math.max(...series.flatMap((s) => s.trajectory!.map((p) => p.day)));
  const merged: Array<Record<string, number | null>> = [];
  for (let d = 0; d <= maxDay; d++) {
    const row: Record<string, number | null> = { day: d };
    for (const s of series) {
      const point = s.trajectory!.find((p) => p.day === d);
      const last = s.trajectory!.filter((p) => p.day <= d).pop();
      const maxOwn = Math.max(...s.trajectory!.map((p) => p.day));
      row[s.key] = d <= maxOwn ? (point?.cases ?? last?.cases ?? null) : null;
    }
    merged.push(row);
  }

  // Sort series so MV Hondius renders last (on top)
  const ordered = [
    ...series.filter((s) => s.key !== "hondius-2026"),
    ...series.filter((s) => s.key === "hondius-2026"),
  ];

  return (
    <div>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[#ef4444]/40 bg-[#ef4444]/10">
          <span className="relative flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-[#ef4444] animate-ping opacity-60" />
            <span className="relative w-2 h-2 rounded-full bg-[#ef4444]" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.18em] font-data text-[#ef4444] font-bold">
            {liveLabel}
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-data ml-1">
          {vsLabel}
        </span>
        {allSeries
          .filter((s) => s.key !== "hondius-2026")
          .map((s) => {
            const isHidden = hidden.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggle(s.key)}
                aria-pressed={!isHidden}
                className={`flex items-center gap-1.5 text-[10px] font-data uppercase tracking-wider px-2 py-1 rounded-full border transition ${
                  isHidden
                    ? "border-border/50 text-muted-foreground/40 opacity-50 hover:opacity-80"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"
                }`}
              >
                <DashSwatch color={isHidden ? "var(--muted-foreground)" : COLOR[s.key]} dash={DASH[s.key]} />
                <span>
                  {s.name} <span className="opacity-60">{s.yearLabel}</span>
                </span>
              </button>
            );
          })}
      </div>

      {liveStartDate && (
        <div className="text-[10px] font-data uppercase tracking-wider text-muted-foreground mb-3 flex flex-wrap gap-x-3 gap-y-1">
          <span>
            {dayLabel} 0 · <span className="text-foreground/80">{dateFmt(0)}</span>
          </span>
          <span className="opacity-30">→</span>
          <span>
            {dayLabel}{" "}
            {Math.max(0, Math.round((Date.now() - liveStartDate.getTime()) / 86400000))}{" "}
            · <span className="text-[#ef4444]">
              {dateFmt(Math.max(0, Math.round((Date.now() - liveStartDate.getTime()) / 86400000)))}
            </span>
          </span>
        </div>
      )}

      <div className="h-[340px] md:h-[400px] -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={merged} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
            <defs>
              <linearGradient id="hondiusFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--border)" strokeDasharray="2 6" vertical={false} opacity={0.5} />
            <XAxis
              dataKey="day"
              tickFormatter={(d) => `${dayLabel.charAt(0).toUpperCase()}${d}`}
              tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: "var(--border)", opacity: 0.5 }}
              minTickGap={28}
              tickMargin={10}
            />
            <YAxis
              tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={32}
            />
            <Tooltip
              cursor={{ stroke: "#ef4444", strokeWidth: 1, strokeDasharray: "3 3" }}
              contentStyle={{
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                fontSize: 12,
              }}
              labelFormatter={(d) => {
                const date = dateFmt(Number(d));
                return date ? `${dayLabel} ${d} · ${date}` : `${dayLabel} ${d}`;
              }}
            />
            {ordered.map((s) =>
              s.key === "hondius-2026" ? (
                <Area
                  key={s.key}
                  type="natural"
                  dataKey={s.key}
                  name={`${s.name} ${s.yearLabel}`}
                  stroke="#ef4444"
                  strokeWidth={3}
                  fill="url(#hondiusFill)"
                  dot={false}
                  activeDot={{ r: 6, fill: "#ef4444", stroke: "var(--background)", strokeWidth: 2 }}
                  connectNulls={false}
                  animationDuration={800}
                />
              ) : (
                <Line
                  key={s.key}
                  type="natural"
                  dataKey={s.key}
                  name={`${s.name} ${s.yearLabel}`}
                  stroke={COLOR[s.key] ?? "var(--muted-foreground)"}
                  strokeWidth={2}
                  strokeDasharray={DASH[s.key]}
                  strokeOpacity={0.85}
                  dot={false}
                  activeDot={{ r: 4, stroke: "var(--background)", strokeWidth: 2 }}
                  connectNulls={false}
                  animationDuration={600}
                />
              )
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DashSwatch({ color, dash }: { color?: string; dash?: string }) {
  // Render a tiny SVG line that mirrors the chart's dash pattern
  return (
    <svg width="18" height="6" viewBox="0 0 18 6" aria-hidden>
      <line
        x1="0"
        y1="3"
        x2="18"
        y2="3"
        stroke={color ?? "currentColor"}
        strokeWidth="2"
        strokeDasharray={dash}
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Custom HTML CFR view — replaces recharts BarChart to fix label clipping
 * and weird scrolling. Each row is a full-width bar with the label above it,
 * so nothing ever gets cut off regardless of language length.
 */
function CfrView({ outbreaks, title, hint }: { outbreaks: Outbreak[]; title: string; hint: string }) {
  const data = [...outbreaks]
    .map((o) => ({
      key: o.key,
      name: o.name,
      year: o.yearLabel,
      cfr: Number(o.cfr.toFixed(1)),
      isCurrent: o.status === "active",
    }))
    .sort((a, b) => b.cfr - a.cfr);

  const max = Math.max(60, ...data.map((d) => d.cfr));

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground mt-1">{hint}</p>
      </div>
      <div className="space-y-3">
        {data.map((d) => {
          const pct = (d.cfr / max) * 100;
          const color = d.isCurrent ? "#ef4444" : COLOR[d.key] ?? "#f97316";
          return (
            <div key={d.key}>
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  {d.isCurrent && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse shrink-0" />
                  )}
                  <span className={`text-sm truncate ${d.isCurrent ? "text-foreground font-semibold" : "text-foreground/85"}`}>
                    {d.name}
                  </span>
                  <span className="text-[11px] text-muted-foreground font-data shrink-0">{d.year}</span>
                </div>
                <span
                  className="text-sm tabular-nums font-data font-semibold shrink-0"
                  style={{ color: d.isCurrent ? "#ef4444" : "var(--foreground)" }}
                >
                  {d.cfr}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-card overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${pct}%`,
                    background: color,
                    opacity: d.isCurrent ? 1 : 0.75,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TableView({
  outbreaks,
  cols,
  activeLabel,
  suspectedCount,
  suspShortLabel,
}: {
  outbreaks: Outbreak[];
  cols: { outbreak: string; cases: string; deaths: string; cfr: string; countries: string };
  activeLabel: string;
  suspectedCount: number;
  suspShortLabel: string;
}) {
  return (
    <div className="space-y-px">
      <div className="hidden md:grid grid-cols-[1.6fr_70px_70px_80px_70px] gap-3 px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-data border-b border-border">
        <span>{cols.outbreak}</span>
        <span className="text-right">{cols.cases}</span>
        <span className="text-right">{cols.deaths}</span>
        <span className="text-right">{cols.cfr}</span>
        <span className="text-right">{cols.countries}</span>
      </div>
      {outbreaks.map((o) => {
        const isCurrent = o.status === "active";
        return (
          <div
            key={o.key}
            className={`grid grid-cols-[1fr_50px_50px_60px_50px] md:grid-cols-[1.6fr_70px_70px_80px_70px] gap-3 items-center px-3 py-3 border-b border-border ${
              isCurrent ? "bg-card/40" : ""
            }`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-sm font-semibold truncate ${isCurrent ? "text-foreground" : "text-foreground/90"}`}>
                  {o.name}
                </span>
                {isCurrent && (
                  <span className="text-[9px] uppercase tracking-wider font-data text-[#ef4444] flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse" />
                    {activeLabel}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5 truncate font-data">
                {o.yearLabel} · {o.variant} · {o.location}
              </div>
              <a
                href={o.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[10px] text-muted-foreground hover:text-foreground hover:underline mt-0.5 block truncate"
                title={o.note}
              >
                ↗ {o.source}
              </a>
            </div>
            <div className="text-right text-sm tabular-nums font-data text-foreground">
              {o.cases + o.deaths}
              {isCurrent && suspectedCount > 0 && (
                <div className="text-[9px] text-[#eab308] font-data leading-tight">
                  +{suspectedCount} {suspShortLabel}
                </div>
              )}
            </div>
            <div className="text-right text-sm tabular-nums font-data text-muted-foreground">{o.deaths}</div>
            <div className={`text-right text-sm tabular-nums font-data font-semibold ${isCurrent ? "text-[#ef4444]" : "text-foreground"}`}>
              {o.cfr.toFixed(1)}%
            </div>
            <div className="text-right text-sm tabular-nums font-data text-muted-foreground">{o.countries}</div>
          </div>
        );
      })}
    </div>
  );
}
